import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import PublicShell from "../../components/PublicShell";
import HeroSearch from "../../components/HeroSearch";
import HouseCard from "../../components/HouseCard";
import usePageSeo from "../../lib/seo";
import { Slider } from "../../components/ui/slider";
import { MapPin } from "lucide-react";

const DESTINATIONS = ["Praia dos Carneiros", "Tamandaré", "Gravatá", "Recife"];

function slugTestId(s) {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, "-");
}

export default function HouseList() {
  const [params, setParams] = useSearchParams();
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceRange, setPriceRange] = useState([500, 5000]);

  const checkIn = params.get("check_in");
  const checkOut = params.get("check_out");
  const guests = params.get("guests");
  const city = params.get("city") || "";

  usePageSeo({
    title: city ? `Casas em ${city}` : "Todas as casas disponíveis",
    description: city
      ? `Casas de férias em ${city}, Pernambuco. Reserva direta pela Azuhomes.`
      : "Casas de férias em Praia dos Carneiros, Tamandaré, Gravatá e Recife. Reserva direta.",
    path: "/casas",
  });

  useEffect(() => {
    const q = new URLSearchParams();
    if (checkIn) q.set("check_in", checkIn);
    if (checkOut) q.set("check_out", checkOut);
    if (guests) q.set("guests", guests);
    if (city) q.set("city", city);
    setLoading(true);
    api.get(`/public/houses?${q.toString()}`).then((r) => {
      setHouses(r.data);
      setLoading(false);
    });
  }, [checkIn, checkOut, guests, city]);

  function setCity(next) {
    const p = new URLSearchParams(params);
    if (next) p.set("city", next); else p.delete("city");
    setParams(p, { replace: true });
  }

  const filtered = houses.filter(
    (h) => h.base_price >= priceRange[0] && h.base_price <= priceRange[1]
  );

  return (
    <PublicShell>
      <section className="pt-12 pb-8 px-6 lg:px-10 bg-white border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
          <span className="text-[11px] uppercase tracking-[0.28em] text-[#066B63] font-semibold">Todas as casas</span>
            <h1 className="font-display text-4xl text-[#22302E] mt-2">Escolha o seu destino.</h1>
          </div>
          <HeroSearch compact />
          <div className="flex flex-wrap gap-2 mt-6">
            <button
              onClick={() => setCity("")}
              data-testid="list-dest-all"
              className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${
                !city ? "border-[#0AA89C] bg-[#0AA89C] text-white" : "border-[#0AA89C]/30 text-[#066B63] hover:bg-[#0AA89C]/10"
              }`}
            >
              Todos os destinos
            </button>
            {DESTINATIONS.map((d) => (
              <button
                key={d}
                onClick={() => setCity(d)}
                data-testid={`list-dest-${slugTestId(d)}`}
                className={`px-3 py-1.5 rounded-full border text-xs transition-colors flex items-center gap-1.5 ${
                  city === d ? "border-[#0AA89C] bg-[#0AA89C] text-white" : "border-[#0AA89C]/30 text-[#066B63] hover:bg-[#0AA89C]/10"
                }`}
              >
                <MapPin className="w-3 h-3" />
                {d}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[220px_1fr] gap-10">
          <aside className="lg:sticky lg:top-24 h-fit">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#22302E]/60 mb-3">Filtros</div>
            <div>
              <div className="text-sm font-medium mb-4">Faixa de preço (por noite)</div>
              <Slider
                data-testid="price-range-slider"
                min={500}
                max={5000}
                step={100}
                value={priceRange}
                onValueChange={setPriceRange}
              />
              <div className="flex items-center justify-between text-xs text-[#22302E]/70 mt-3">
                <span>R$ {priceRange[0].toLocaleString("pt-BR")}</span>
                <span>R$ {priceRange[1].toLocaleString("pt-BR")}</span>
              </div>
            </div>
          </aside>

          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="text-sm text-[#22302E]/70" data-testid="results-count">
                {loading ? "Carregando…" : `${filtered.length} casa(s) ${city ? `em ${city}` : "disponíveis"}`}
              </div>
            </div>
            {loading ? (
              <div className="grid md:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="animate-pulse h-96 bg-[#F7F4EF] rounded-md" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="border border-dashed border-[#0AA89C]/30 rounded-md p-16 text-center text-[#22302E]/60">
                Nenhuma casa disponível para os critérios selecionados.
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {filtered.map((h) => (
                  <HouseCard key={h.id} house={h} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
